import React from "react";

import {
  Box,
  IconButton,
  Typography
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

import { Variable } from "@/types";
import { HelpBox } from "@/components/atoms";

interface VariableSelectionProps {
  variable: Variable;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  icon?: React.ReactElement;
  onDelete?: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}

const VariableChip: React.FC<VariableSelectionProps> = ({
  variable,
  color = 'primary',
  icon,
  onDelete,
  disabled = false,
}) => {
  const theme = useTheme();

  const toHeader = (variable: Variable) => {
    if (variable.tag_type == 'Lifestyle Statements') {
      return variable.tag_name;
    } else {
      return variable.tag_category;
    }

  }

  const toLabel = (variable: Variable) => {
    if (variable.tag_type == 'Lifestyle Statements') {
      return variable.tag_category;
    } else {
      return variable.tag_name;
    }
  }

  const headerText = toHeader(variable);
  const labelText = toLabel(variable);

  const paletteColor = theme.palette[color];

  return (
    <HelpBox
      key={variable.tag_id}
      title={`${variable.tag_type}  | ${variable.tag_group} | ${variable.tag_category} | ${variable.tag_name}`}
      maxWidth={300}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: '8px',
          p: '8px 10px',
          my: 0.5,
          opacity: disabled ? theme.palette.action.disabledOpacity : 1,
          backgroundColor: alpha(paletteColor.main, 0.12),
          border: `1px solid ${alpha(paletteColor.main, 0.22)}`,
          // borderLeft: `3px solid ${paletteColor.main}`,
          backdropFilter: 'blur(4px)',
          transition: theme.transitions.create(['background-color', 'border-color'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >

        {icon && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mr: 1.25,
              color: paletteColor.main,
              '& svg': {
                fontSize: 16,
              },
            }}
          >
            {icon}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flex: 1,
            minWidth: 0,
            mr: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 8,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: paletteColor.main,
              opacity: 0.9,
              lineHeight: 1.2,
              mb: 0.25
            }}
          >
            {headerText}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              lineHeight: 1.3,
              color: theme.palette.text.primary,
              wordBreak: 'break-word'
            }}
          >
            {labelText}
          </Typography>
        </Box>

        {onDelete && (
          <IconButton
            size="small"
            aria-label="delete chip"
            onClick={onDelete}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            sx={{
              ml: 'auto',
              flexShrink: 0,
              p: '3px',
              color: theme.palette.text.secondary,
              opacity: 0.75,
              transition: theme.transitions.create(['opacity', 'color', 'background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '&:hover': {
                color: theme.palette.common.white,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
              },
              '& svg': {
                fontSize: 16,
              },
            }}
          >
            <Cancel />
          </IconButton>
        )}

      </Box>
    </HelpBox>
  );
};

export default VariableChip;
